import { NavigationTabsItem } from '@app/components/ui/navigation-tabs/navigation-tabs';
import { OptionItem } from '@app/components/ui/select/select.type';
import useTranslation from '@app/hooks/use-translation';
import {
  StatisticWidgetsBulkRequestType,
  StatisticWidgetsRangeType,
} from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';
import {
  useGetCommandsBulkQuery,
  useLazyGetCommandOperatorsListQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-commands.api';
// import { useGetMonitoringFormsBulkQuery } from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { useGetStatisticWidgetsQuery } from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';
import { useGetUsersListWithRolesQuery } from '@app/store/api/qm-module/qm-users/qm-users.api';
// import { useGetStatisticMFListQuery } from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';
import { Loading, NavigationTabs, PageHeader } from '@ui';
import React, { useCallback, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import { StatisticOutletContextType } from './type';
import { StatisticWidgetsFilterBlock } from './blocks/statistic-widgets-filter-block';

const main_pathname = '/qm_statistic';
const QM_STATISTIC_RANGE_STORAGE_KEY = 'QM_STATISTIC_RANGE_STORAGE';

const QmStatisticOutlet = () => {
  const { t } = useTranslation('pages.qmManage.statistic');
  const location = useLocation();
  const navigate = useNavigate();

  const rangeFromStor = localStorage.getItem(QM_STATISTIC_RANGE_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(QM_STATISTIC_RANGE_STORAGE_KEY) || '')
    : (undefined as StatisticWidgetsRangeType | undefined);

  const [widgetsFilterBlock, changeWidgetsFilterBlock] = useState<
    Partial<StatisticWidgetsBulkRequestType>
  >({
    range: {
      ...(localStorage.getItem(QM_STATISTIC_RANGE_STORAGE_KEY)
        ? rangeFromStor
        : { type: 'cm', parameters: [] }),
      dt_type: 'start',
    },
  });
  const [operatorOptions, setOperatorOptions] = useState<OptionItem[]>([]);

  // const { data: monitoringForms, isLoading: monitFormsLoading } = useGetMonitoringFormsBulkQuery(
  //   {},
  // );
  const [getTeamOperators] = useLazyGetCommandOperatorsListQuery();
  const { data: userInfo } = useGetUserInfoQuery();
  const { qm_role } = userInfo || {};
  const { data: widgetsList, isLoading: widgetsLoading } =
    useGetStatisticWidgetsQuery(widgetsFilterBlock);

  const { data: controllersList, isLoading: controllersLoading } = useGetUsersListWithRolesQuery(
    {
      limit: 100,
      offset: 0,
      role_qm: 'controller',
    },
    { skip: qm_role !== 'manager' },
  );

  const { data: teamsList, isLoading: teamListLoading } = useGetCommandsBulkQuery(undefined, {
    skip: qm_role === 'operator',
  });

  const [navigationItems, setNavigationItems] = useState<NavigationTabsItem[]>([
    {
      index: 'main',
      link: './.',
      label: t('main_tap_label'),
      icon: 'ChartIcon',
      deleteException: true,
    },
  ]);

  const teamsOptions: OptionItem[] =
    teamsList?.qm_teams.map(({ name, team_id }) => ({
      title: name,
      value: team_id,
    })) || [];

  const controllersOptions: OptionItem[] =
    controllersList?.qm_users.map(({ id, username }) => ({
      title: username,
      value: id,
    })) || [];

  const getOperatorsByTeams = useCallback(
    async (teams: string[]) => {
      const operators: OptionItem[] = [];
      for (const element of teams) {
        const res = (
          await getTeamOperators({ team_id: element }).unwrap()
        ).operators?.map<OptionItem>((op) => ({
          value: op.user_id,
          title: op.name,
        }));
        if (res) {
          operators.push(...res); // добавляем элементы в массив
        }
      }
      return operators;
    },
    [getTeamOperators],
  );

  const statisticOutletContext: StatisticOutletContextType = {
    widgetsList,
    widgetsFilterBlock,
    navigationItems,
    setNavigationItems,
    controllersOptions,
    teamsList,
    getOperatorsByTeams: getOperatorsByTeams, // не достается из контекста ниже
  };

  const pageHeaderLabels = {
    monitoring: t('widget.accordion_label_button_monitoring'),
    teams: t('widget.accordion_label_button_teams_details'),
    operators: t('widget.accordion_label_button_operator_details'),
  };

  const isLoading = widgetsLoading || teamListLoading || controllersLoading;
  if (isLoading) return <Loading />;
  return (
    <>
      <PageHeader
        icon="ChartIcon"
        label={pageHeaderLabels?.[location.pathname.split('/')[2]] || t('pageHeader')}
      />
      <NavigationTabs
        onDeleteTab={(index) => {
          setNavigationItems((prev) => prev.filter((i) => i.index !== index));
          navigate('./.');
        }}
        overflow
        items={navigationItems}
      />
      <div className="flex flex-col gap-[20px]">
        {location.pathname === main_pathname && (
          <StatisticWidgetsFilterBlock
            operatorOptions={operatorOptions}
            setOperatorOptions={setOperatorOptions}
            getOperatorsByTeams={getOperatorsByTeams}
            changeWidgetsFilterBlock={changeWidgetsFilterBlock}
            widgetsFilterBlock={widgetsFilterBlock}
            teamsOptions={teamsOptions}
          />
        )}
        <Outlet context={statisticOutletContext} />
      </div>
    </>
  );
};

export default QmStatisticOutlet;
